import { useState, useEffect, useRef } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown, BookmarkPlus, GitCompare, Check, Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CandidateCard } from '../components/CandidateCard';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import PageHelp from '../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 18;
const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

// ── Access gate — shown to guests (must register) and logged-in unpaid brands ──
const GalleryGate = ({ variant = 'guest', onPrimary, onSecondary }) => {
  const isUnpaid = variant === 'unpaid';
  return (
    <div className="relative min-h-[calc(100vh-72px)] bg-white overflow-hidden flex items-center justify-center px-6 py-16">
      {/* Faded blurred backdrop — purely decorative, fixed to the container (adds no scroll height) */}
      <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-6 p-8 blur-[7px] opacity-[0.12] pointer-events-none select-none" aria-hidden="true">
        {[...Array(8)].map((_, i) => (
          <div key={i}>
            <div className="aspect-[3/4] bg-secondary mb-3" />
            <div className="h-3 bg-secondary rounded w-3/4 mb-2" />
            <div className="h-2.5 bg-secondary rounded w-1/2" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/75 to-white" aria-hidden="true" />

      {/* Centered gate card */}
      <div className="relative z-10 bg-white border border-border p-9 lg:p-12 max-w-md w-full shadow-[8px_8px_0px_hsl(var(--border))] text-center">
        <div className="w-11 h-11 border border-border flex items-center justify-center mx-auto mb-6">
          <Lock className="w-[18px] h-[18px] text-[hsl(var(--primary))]" />
        </div>

        <p className="text-[hsl(var(--primary))] mb-4" style={{ fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.22em' }}>
          {isUnpaid ? '✦ ACCOUNT ACTIVE' : '✦ MEMBERS ONLY · THE SPY GALLERY'}
        </p>

        <h2 className="text-[28px] leading-tight font-semibold mb-3 text-foreground" style={{ fontFamily: SERIF }}>
          {isUnpaid ? "You're in. Now choose your plan." : 'The industry, scouted for you.'}
        </h2>

        <p className="text-muted-foreground text-sm leading-relaxed mb-7">
          {isUnpaid
            ? 'Activate a subscription to unlock the full Spy Gallery — verified profiles, discipline & culture filters, AI role-fit ranking and your hiring pipeline.'
            : 'FashionSpyder gives in-house hiring teams direct access to verified designers, creatives and craftspeople across the world’s fashion capitals. Create your account to choose a plan — or sign in to continue.'}
        </p>

        {!isUnpaid && (
          <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground mb-7" style={{ fontFamily: MONO, letterSpacing: '0.04em' }}>
            <span>Verified talent</span>
            <span className="text-[hsl(var(--primary))]">✦</span>
            <span>AI role-fit</span>
            <span className="text-[hsl(var(--primary))]">✦</span>
            <span>Global cities</span>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          <Button
            onClick={onPrimary}
            className="w-full rounded-none bg-foreground text-background hover:bg-foreground/85 gap-2 h-11"
            data-testid="gallery-gate-primary-btn"
          >
            <Sparkles className="w-4 h-4" /> {isUnpaid ? 'View Plans' : 'Get Started — Free to join'}
          </Button>
          <Button
            variant="outline"
            onClick={onSecondary}
            className="w-full rounded-none border-border hover:border-foreground gap-2 h-11"
            data-testid="gallery-gate-secondary-btn"
          >
            {isUnpaid ? 'Back to Dashboard' : 'I already have an account'} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed" style={{ fontFamily: MONO }}>
          For fashion brand hiring teams only. Recruitment agencies are not eligible.
        </p>
      </div>
    </div>
  );
};

// ── Recently Viewed (session storage) ────────────────────────────────────────
const getRecentlyViewed = () => {
  try { return JSON.parse(sessionStorage.getItem('recentlyViewed') || '[]'); }
  catch { return []; }
};

// ── Multi-select dropdown ─────────────────────────────────────────────────────
const MultiFilterSelect = ({ label, values, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">{label}</label>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between border px-3 py-2 text-sm bg-white cursor-pointer transition-colors text-left ${
          values.length > 0 ? 'border-foreground text-foreground' : 'border-border text-muted-foreground'
        }`}
      >
        <span className="truncate pr-1">
          {values.length === 0 ? 'All' : values.length === 1 ? values[0] : `${values.length} selected`}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 w-full min-w-[160px] bg-white border border-border shadow-lg max-h-52 overflow-y-auto">
          {options.map(opt => (
            <label
              key={opt}
              className="flex items-center gap-2.5 px-3 py-2 hover:bg-secondary cursor-pointer"
            >
              <div className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-colors ${
                values.includes(opt) ? 'bg-foreground border-foreground' : 'border-border'
              }`}>
                {values.includes(opt) && <Check className="w-2.5 h-2.5 text-background" />}
              </div>
              <span className="text-sm">{opt}</span>
            </label>
          ))}
          {values.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-2 text-xs text-muted-foreground hover:text-foreground border-t border-border"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Dual experience range slider ──────────────────────────────────────────────
const ExpRangeSlider = ({ expMin, expMax, onChange }) => {
  const MAX = 20;
  const min = parseInt(expMin) || 0;
  const max = parseInt(expMax) || MAX;
  const leftPct = (min / MAX) * 100;
  const maxPct = (max / MAX) * 100;

  const handleMin = (e) => {
    const v = parseInt(e.target.value);
    if (v <= max) onChange(v === 0 ? '' : String(v), expMax);
  };
  const handleMax = (e) => {
    const v = parseInt(e.target.value);
    if (v >= min) onChange(expMin, v === MAX ? '' : String(v));
  };

  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">
        Exp: {min}–{max === MAX ? `${MAX}+` : max} yrs
      </label>
      <div className="relative h-6 flex items-center mt-1">
        {/* Background track */}
        <div className="absolute left-0 right-0 h-1 bg-border rounded-full pointer-events-none" />
        {/* Active range fill */}
        <div
          className="absolute h-1 bg-foreground rounded-full pointer-events-none"
          style={{ left: `${leftPct}%`, right: `${100 - maxPct}%` }}
        />
        {/* Min thumb handle (visual) */}
        <div
          className="absolute w-3.5 h-3.5 bg-foreground border-2 border-white rounded-full shadow pointer-events-none z-10"
          style={{ left: `calc(${leftPct}% - 7px)` }}
        />
        {/* Max thumb handle (visual) */}
        <div
          className="absolute w-3.5 h-3.5 bg-foreground border-2 border-white rounded-full shadow pointer-events-none z-10"
          style={{ left: `calc(${maxPct}% - 7px)` }}
        />
        {/* Min range input */}
        <input
          type="range" min="0" max={MAX} value={min}
          onChange={handleMin}
          className="absolute w-full h-1 opacity-0 cursor-pointer"
          style={{ zIndex: min > MAX - 3 ? 5 : 3 }}
        />
        {/* Max range input */}
        <input
          type="range" min="0" max={MAX} value={max}
          onChange={handleMax}
          className="absolute w-full h-1 opacity-0 cursor-pointer"
          style={{ zIndex: 4 }}
        />
      </div>
    </div>
  );
};

// ── Filter chip ───────────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <span className="flex items-center gap-1.5 text-xs border border-foreground px-2.5 py-1">
    {label}
    <button onClick={onRemove} className="hover:opacity-60"><X className="w-3 h-3" /></button>
  </span>
);

// ── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="animate-pulse">
    <div className="aspect-[3/4] bg-secondary mb-4" />
    <div className="h-4 bg-secondary rounded w-3/4 mb-2" />
    <div className="h-3 bg-secondary rounded w-1/2 mb-2" />
    <div className="h-3 bg-secondary rounded w-2/3" />
  </div>
);

// ── Main Gallery ──────────────────────────────────────────────────────────────
const Gallery = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState(getRecentlyViewed);
  const offsetRef = useRef(0);

  // Compare mode
  const [compareMode, setCompareMode] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  // Bulk select
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState(false);

  // Drag-to-pipeline
  const [isDragging, setIsDragging] = useState(false);
  const [dragCandidate, setDragCandidate] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // AI Talent Brief (INTELLIGENCE)
  const [briefText, setBriefText] = useState('');
  const [briefResults, setBriefResults] = useState(null); // null = not active, [] = no results
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState('');

  // Rank-by-fit context (INTELLIGENCE) — ranks the gallery by affinity-aware role fit
  const [fitContext, setFitContext] = useState(null); // {kind:'brief'|'role', id?, role?, label}
  const [myBriefs, setMyBriefs] = useState([]);
  const [fitMenuOpen, setFitMenuOpen] = useState(false);
  const [customRole, setCustomRole] = useState('');

  // All categorical filters are now arrays for multi-select
  const [filters, setFilters] = useState({
    role: [], seniority: [], location_city: [], location_country: [],
    availability: [], work_preference: [], discipline: [], cultural_tag: [],
    exp_min: '', exp_max: '',
    sort_by: 'overall_ranking',
  });
  const [filterOptions, setFilterOptions] = useState({
    roles: [], seniorities: [], cities: [], countries: [],
    availabilities: [], work_preferences: ['Remote', 'Hybrid', 'On-site'],
    disciplines: [], cultural_tags: [],
  });

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load filter meta-options
  useEffect(() => {
    authFetch(`${BACKEND}/api/candidates/filters/meta`)
      .then(r => r.json())
      .then(d => { if (d.success) setFilterOptions(d); })
      .catch(() => {});
  }, []);

  // Load the user's active Talent Briefs for the "rank by fit" selector
  useEffect(() => {
    if (user?.subscription_plan !== 'intelligence') return;
    authFetch(`${BACKEND}/api/talent-briefs`)
      .then(r => r.json())
      .then(d => { if (d.success) setMyBriefs((d.data || []).filter(b => b.status === 'active')); })
      .catch(() => {});
  }, [user]);

  // Build URL params — appends each array value as a repeated param
  const buildParams = (offset = 0) => {
    const p = new URLSearchParams({ limit: PAGE_SIZE, offset, sort_by: filters.sort_by });
    if (debouncedSearch) p.set('search', debouncedSearch);
    ['role', 'seniority', 'location_city', 'location_country', 'availability', 'work_preference', 'discipline', 'cultural_tag'].forEach(key => {
      (filters[key] || []).forEach(v => p.append(key, v));
    });
    if (filters.exp_min) p.set('exp_min', filters.exp_min);
    if (filters.exp_max) p.set('exp_max', filters.exp_max);
    if (fitContext?.kind === 'brief') p.set('fit_brief_id', fitContext.id);
    else if (fitContext?.kind === 'role') p.set('fit_role', fitContext.role);
    return p.toString();
  };

  // Serialise filter state for effect dependency
  const filtersKey = JSON.stringify(filters);
  const fitKey = JSON.stringify(fitContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(`${BACKEND}/api/candidates?${buildParams(0)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.success) {
          setCandidates(d.data);
          setTotal(d.total || 0);
          offsetRef.current = d.data.length;
          setHasMore(d.data.length === PAGE_SIZE);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch, filtersKey, fitKey]); // eslint-disable-line

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    authFetch(`${BACKEND}/api/candidates?${buildParams(offsetRef.current)}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setCandidates(prev => [...prev, ...d.data]);
          offsetRef.current += d.data.length;
          setHasMore(d.data.length === PAGE_SIZE);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const toggleMultiFilter = (key, value) => {
    setFilters(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const clearFilters = () => {
    setFilters({
      role: [], seniority: [], location_city: [], location_country: [],
      availability: [], work_preference: [], discipline: [], cultural_tag: [],
      exp_min: '', exp_max: '',
      sort_by: 'overall_ranking',
    });
    setSearch('');
    setDebouncedSearch('');
  };

  // Compare
  const toggleCompare = (candidate) => {
    setCompareList(prev => {
      if (prev.find(c => c.id === candidate.id)) return prev.filter(c => c.id !== candidate.id);
      if (prev.length >= 3) return prev; // max 3
      return [...prev, candidate];
    });
  };

  // Bulk select
  const toggleBulkSelect = (candidate) => {
    setBulkSelected(prev =>
      prev.find(c => c.id === candidate.id)
        ? prev.filter(c => c.id !== candidate.id)
        : [...prev, candidate]
    );
  };

  const bulkAddToPipeline = async () => {
    setBulkAdding(true);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker/add-to-pipeline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: bulkSelected.map(c => c.id), stage: 'saved' }),
      });
      if (res.status === 401) { navigate('/login'); return; }
      setBulkSuccess(true);
      setTimeout(() => {
        setBulkSelected([]);
        setBulkMode(false);
        setBulkSuccess(false);
      }, 1800);
    } catch {}
    finally { setBulkAdding(false); }
  };

  // Drag-to-pipeline handlers
  const PIPELINE_STAGES = {
    saved: 'Saved',
    contacted: 'Contacted',
    in_process: 'In Process',
    hired: 'Hired',
    archived: 'Archived',
  };

  const handleCardDragStart = (candidate) => {
    setIsDragging(true);
    setDragCandidate(candidate);
    setDropTarget(null);
  };

  const handleCardDragEnd = () => {
    setIsDragging(false);
    setDragCandidate(null);
    setDropTarget(null);
  };

  const handleDropOnStage = async (stage) => {
    if (!dragCandidate) return;
    const stageName = PIPELINE_STAGES[stage] || stage;
    const candidateName = dragCandidate.full_name || 'Candidate';
    setIsDragging(false);
    setDropTarget(null);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker/add-to-pipeline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: [dragCandidate.id], stage }),
      });
      if (res.ok) {
        toast.success(`${candidateName} added to ${stageName}`);
      } else {
        toast.error('Could not add to pipeline');
      }
    } catch {
      toast.error('Could not add to pipeline');
    }
    setDragCandidate(null);
  };

  // AI Talent Brief handler
  const runBrief = async () => {
    if (briefText.trim().length < 50) {
      setBriefError('Please describe the role or candidate in at least 50 characters.');
      return;
    }
    setBriefError('');
    setBriefLoading(true);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_description: briefText, top_n: 18 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Matchmaker error');
      setBriefResults(data.results || []);
    } catch (err) {
      setBriefError(err.message || 'Could not process brief');
    } finally {
      setBriefLoading(false);
    }
  };

  const clearBrief = () => {
    setBriefResults(null);
    setBriefText('');
    setBriefError('');
  };

  // Count active filters
  const activeCount =
    ['role', 'seniority', 'location_city', 'location_country', 'availability', 'work_preference', 'discipline', 'cultural_tag']
      .reduce((acc, k) => acc + (filters[k]?.length || 0), 0)
    + (filters.exp_min || filters.exp_max ? 1 : 0)
    + (debouncedSearch ? 1 : 0);

  // "New this week" threshold
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const handleViewed = (cand) => {
    const current = getRecentlyViewed().filter(v => v.id !== cand.id);
    const updated = [
      { id: cand.id, full_name: cand.full_name, core_role: cand.core_role, photo_url: cand.photo_url },
      ...current,
    ].slice(0, 10);
    sessionStorage.setItem('recentlyViewed', JSON.stringify(updated));
    setRecentlyViewed(updated);
  };

  // Gate: unauthenticated visitors see the waitlist prompt (after all hooks)
  if (authLoading) return null;

  const gallerySEO = (
      <SEO
      title="Fashion Talent Gallery — Verified Professionals"
      description="Browse FashionSpyder's curated gallery of verified fashion professionals. Womenswear designers, art directors, photographers, stylists and more from Milan, Paris, London and beyond."
      path="/gallery"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'FashionSpyder Talent Gallery',
        description: 'Verified fashion professionals available for hire.',
        url: 'https://fashionspyder.com/gallery',
        provider: { '@type': 'Organization', name: 'FashionSpyder' },
      }}
    />
  );

  if (!user) {
    return (
      <>
        {gallerySEO}
        <GalleryGate
          variant="guest"
          onPrimary={() => navigate('/register')}
          onSecondary={() => navigate('/login')}
        />
      </>
    );
  }

  // Double-verification gate: logged-in brands without an active subscription
  // cannot access the gallery until they subscribe. Admins bypass.
  const ADMIN_EMAILS = ['info@flence.com', 'admin@fashionspyder.com'];
  const isAdmin = ADMIN_EMAILS.includes((user.email || '').toLowerCase());
  const hasActivePlan = ['recon', 'intelligence'].includes(user.subscription_plan);
  if (!hasActivePlan && !isAdmin) {
    return (
      <>
        {gallerySEO}
        <GalleryGate
          variant="unpaid"
          onPrimary={() => navigate('/pricing')}
          onSecondary={() => navigate('/dashboard')}
        />
      </>
    );
  }

  const isIntelligence = user?.subscription_plan === 'intelligence';

  return (
    <div className="min-h-screen bg-white">
      {gallerySEO}
      {/* Hero */}
      <section className="py-10 lg:py-28 px-4 sm:px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-3">Talent Gallery</h1>
          <p className="text-sm sm:text-lg text-muted-foreground mb-6 sm:mb-10 max-w-2xl">
            {!loading && total > 0 ? `${total} verified fashion professionals` : 'Discover verified fashion professionals'}
          </p>
          <div className="flex gap-2 sm:gap-3 max-w-3xl flex-wrap">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, role..."
                className="pl-9 sm:pl-12 h-auto py-3 sm:py-4 rounded-none border-border text-sm"
                data-testid="gallery-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 sm:px-5 h-auto py-3 sm:py-4 rounded-none border-border flex items-center gap-1.5 sm:gap-2 text-sm"
              data-testid="filters-toggle-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeCount > 0 && (
                <Badge className="bg-foreground text-background text-xs px-1.5 py-0.5">{activeCount}</Badge>
              )}
            </Button>
            {isIntelligence ? (
              <Button
                variant={compareMode ? 'default' : 'outline'}
                onClick={() => { setCompareMode(!compareMode); setBulkMode(false); if (compareMode) setCompareList([]); }}
                className={`px-3 sm:px-5 h-auto py-3 sm:py-4 rounded-none flex items-center gap-1.5 sm:gap-2 text-sm ${compareMode ? 'bg-foreground text-background' : 'border-border'}`}
                data-testid="compare-mode-btn"
              >
                <GitCompare className="w-4 h-4" />
                <span className="hidden sm:inline">Compare</span>
                {compareList.length > 0 && <span>({compareList.length})</span>}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="px-3 sm:px-5 h-auto py-3 sm:py-4 rounded-none border-border flex items-center gap-1.5 sm:gap-2 opacity-60 text-sm"
                data-testid="compare-mode-locked-btn"
                title="Compare available on INTELLIGENCE plan"
              >
                <Lock className="w-4 h-4" />
                <span className="hidden sm:inline">Compare</span>
              </Button>
            )}
            <Button
              variant={bulkMode ? 'default' : 'outline'}
              onClick={() => { setBulkMode(!bulkMode); setCompareMode(false); if (bulkMode) setBulkSelected([]); }}
              className={`px-3 sm:px-5 h-auto py-3 sm:py-4 rounded-none flex items-center gap-1.5 sm:gap-2 text-sm ${bulkMode ? 'bg-foreground text-background' : 'border-border'}`}
              data-testid="bulk-mode-btn"
            >
              <BookmarkPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Bulk Add</span>
              {bulkSelected.length > 0 && <span>({bulkSelected.length})</span>}
            </Button>
            <PageHelp pageKey="gallery" />
          </div>

          {/* AI Talent Brief — INTELLIGENCE feature */}
          <div className="mt-5 sm:mt-6 max-w-3xl">
            {isIntelligence ? (
              <div data-testid="talent-brief-section">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    AI Talent Brief
                  </span>
                  <span className="text-xs border border-foreground/20 px-1.5 py-0.5 text-muted-foreground"
                    style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.1em' }}>
                    INTELLIGENCE
                  </span>
                  {briefResults !== null && (
                    <button onClick={clearBrief}
                      className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                      <X className="w-3 h-3" /> Clear brief
                    </button>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={briefText}
                    onChange={e => { setBriefText(e.target.value); setBriefError(''); }}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && runBrief()}
                    placeholder='e.g. "bilingual luxury buyer Paris, 5+ years..."'
                    className="flex-1 border border-border px-4 py-3 text-sm bg-white focus:outline-none focus:border-foreground transition-colors"
                    data-testid="talent-brief-input"
                    disabled={briefLoading}
                  />
                  <Button
                    onClick={runBrief}
                    disabled={briefLoading || briefText.trim().length < 50}
                    className="rounded-none bg-foreground text-background hover:bg-foreground/80 px-6 shrink-0"
                    data-testid="talent-brief-submit"
                  >
                    {briefLoading ? 'Finding...' : 'Find Matches'}
                  </Button>
                </div>
                {briefError && (
                  <p className="text-red-600 text-xs mt-2">{briefError}</p>
                )}
                {briefResults !== null && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {briefResults.length > 0
                      ? `${briefResults.length} candidates ranked by match score`
                      : 'No candidates matched this brief.'}
                  </p>
                )}
              </div>
            ) : (
              <div data-testid="talent-brief-locked">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground/50"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    AI Talent Brief
                  </span>
                  <span className="text-xs border border-border px-1.5 py-0.5 text-muted-foreground/40 flex items-center gap-1"
                    style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px' }}>
                    <Lock className="w-2.5 h-2.5" /> INTELLIGENCE
                  </span>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full flex items-center gap-3 border border-dashed border-border px-4 py-3 text-sm text-muted-foreground/50 hover:border-foreground/30 hover:text-muted-foreground transition-colors text-left"
                  data-testid="talent-brief-locked-btn"
                >
                  <Lock className="w-4 h-4 flex-shrink-0" />
                  <span>AI brief matching — available on INTELLIGENCE</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Filter Panel */}
      {showFilters && (
        <section className="border-b border-border bg-secondary/20 px-6 lg:px-20 py-8">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center justify-between mb-5">
              <p className="font-semibold text-sm uppercase tracking-wide">Filters</p>
              {activeCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 mb-6">
              {/* Basic filters — all tiers */}
              <MultiFilterSelect
                label="Role"
                values={filters.role}
                options={filterOptions.roles}
                onChange={v => setFilters(p => ({ ...p, role: v }))}
              />
              <MultiFilterSelect
                label="Seniority"
                values={filters.seniority}
                options={filterOptions.seniorities}
                onChange={v => setFilters(p => ({ ...p, seniority: v }))}
              />
              <MultiFilterSelect
                label="City"
                values={filters.location_city}
                options={filterOptions.cities}
                onChange={v => setFilters(p => ({ ...p, location_city: v }))}
              />
              <MultiFilterSelect
                label="Discipline"
                values={filters.discipline}
                options={filterOptions.disciplines}
                onChange={v => setFilters(p => ({ ...p, discipline: v }))}
              />
              {/* Advanced filters — INTELLIGENCE only */}
              {isIntelligence ? (
                <>
                  <MultiFilterSelect
                    label="Country"
                    values={filters.location_country}
                    options={filterOptions.countries}
                    onChange={v => setFilters(p => ({ ...p, location_country: v }))}
                  />
                  <MultiFilterSelect
                    label="Availability"
                    values={filters.availability}
                    options={filterOptions.availabilities}
                    onChange={v => setFilters(p => ({ ...p, availability: v }))}
                  />
                  <MultiFilterSelect
                    label="Work Mode"
                    values={filters.work_preference}
                    options={filterOptions.work_preferences}
                    onChange={v => setFilters(p => ({ ...p, work_preference: v }))}
                  />
                  <MultiFilterSelect
                    label="Cultural Tags"
                    values={filters.cultural_tag}
                    options={filterOptions.cultural_tags}
                    onChange={v => setFilters(p => ({ ...p, cultural_tag: v }))}
                  />
                  <ExpRangeSlider
                    expMin={filters.exp_min}
                    expMax={filters.exp_max}
                    onChange={(min, max) => setFilters(p => ({ ...p, exp_min: min, exp_max: max }))}
                  />
                </>
              ) : (
                /* Locked placeholders for RECON */
                ['Country', 'Availability', 'Work Mode', 'Experience'].map(label => (
                  <button
                    key={label}
                    onClick={() => navigate('/pricing')}
                    className="relative opacity-60"
                    title={`${label} filter requires INTELLIGENCE plan`}
                    data-testid={`filter-locked-${label.toLowerCase()}`}
                  >
                    <label className="block text-xs text-muted-foreground mb-1.5 uppercase tracking-wide pointer-events-none">{label}</label>
                    <div className="w-full flex items-center justify-between border border-border px-3 py-2 text-sm bg-white text-muted-foreground cursor-pointer hover:border-foreground/30 transition-colors">
                      <span className="truncate pr-1">All</span>
                      <Lock className="w-3 h-3 flex-shrink-0" />
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Sort:</span>
              {[
                ['overall_ranking', 'Top Ranked'],
                ['created_at', 'Newest'],
                ['years_experience', 'Most Experienced'],
              ].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setFilters(p => ({ ...p, sort_by: val }))}
                  className={`text-sm px-3 py-1 border transition-colors ${
                    filters.sort_by === val
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border text-muted-foreground hover:border-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
              {!isIntelligence && (
                <span className="text-xs text-muted-foreground border border-border px-2 py-0.5 ml-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Advanced filters on INTELLIGENCE
                </span>
              )}

              {/* Rank by fit (INTELLIGENCE) — affinity-aware role-fit ranking */}
              {isIntelligence && (
                <div className="relative ml-1" data-testid="rank-by-fit">
                  <button
                    onClick={() => setFitMenuOpen(o => !o)}
                    className={`text-sm px-3 py-1 border flex items-center gap-1.5 transition-colors ${
                      fitContext ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-border text-muted-foreground hover:border-foreground'
                    }`}
                    data-testid="rank-by-fit-trigger"
                  >
                    <Sparkles className="w-3 h-3" />
                    {fitContext ? `Fit: ${fitContext.label}` : 'Rank by fit'}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {fitMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setFitMenuOpen(false)} />
                      <div className="absolute right-0 mt-1 w-72 bg-white border border-border shadow-xl z-50 p-3" data-testid="rank-by-fit-menu">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.12em' }}>
                          Score every candidate for…
                        </p>
                        {myBriefs.length > 0 ? (
                          <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                            {myBriefs.map(b => (
                              <button
                                key={b.id}
                                onClick={() => { setFitContext({ kind: 'brief', id: b.id, label: b.role_title }); setFitMenuOpen(false); }}
                                className="w-full text-left text-sm px-2 py-1.5 hover:bg-secondary transition-colors flex items-center justify-between"
                                data-testid={`fit-brief-${b.id}`}
                              >
                                <span className="truncate">{b.role_title}</span>
                                {b.seniority && <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">{b.seniority}</span>}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground mb-3">No active Talent Briefs yet — or score a custom role below.</p>
                        )}
                        <div className="flex items-center gap-1.5 border border-border focus-within:border-foreground">
                          <input
                            value={customRole}
                            onChange={e => setCustomRole(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && customRole.trim()) { setFitContext({ kind: 'role', role: customRole.trim(), label: customRole.trim() }); setFitMenuOpen(false); } }}
                            placeholder="Custom role — e.g. Art Director…"
                            className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent"
                            data-testid="fit-custom-role-input"
                          />
                          <button
                            onClick={() => { if (customRole.trim()) { setFitContext({ kind: 'role', role: customRole.trim(), label: customRole.trim() }); setFitMenuOpen(false); } }}
                            disabled={!customRole.trim()}
                            className="px-2.5 py-1.5 text-foreground hover:bg-secondary disabled:opacity-30"
                            data-testid="fit-custom-role-submit"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Active filter chips */}
      {activeCount > 0 && (
        <div className="px-6 lg:px-20 py-3 bg-white border-b border-border">
          <div className="max-w-[1200px] mx-auto flex flex-wrap gap-2">
            {debouncedSearch && (
              <FilterChip
                label={`"${debouncedSearch}"`}
                onRemove={() => { setSearch(''); setDebouncedSearch(''); }}
              />
            )}
            {['role', 'seniority', 'location_city', 'location_country', 'availability', 'work_preference', 'discipline', 'cultural_tag'].map(key =>
              (filters[key] || []).map(v => (
                <FilterChip
                  key={`${key}-${v}`}
                  label={v}
                  onRemove={() => toggleMultiFilter(key, v)}
                />
              ))
            )}
            {(filters.exp_min || filters.exp_max) && (
              <FilterChip
                label={`${filters.exp_min || '0'}–${filters.exp_max || '20+'} yrs`}
                onRemove={() => setFilters(p => ({ ...p, exp_min: '', exp_max: '' }))}
              />
            )}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {bulkMode && bulkSelected.length > 0 && (
        <div className={`px-6 lg:px-20 py-3 transition-colors ${bulkSuccess ? 'bg-green-700' : 'bg-foreground'} text-background`}>
          <div className="max-w-[1200px] mx-auto flex items-center justify-between">
            <span className="text-sm">
              {bulkSuccess ? `✓ ${bulkSelected.length} candidates added to pipeline!` : `${bulkSelected.length} candidates selected`}
            </span>
            {!bulkSuccess && (
              <div className="flex gap-3">
                <Button
                  onClick={bulkAddToPipeline}
                  disabled={bulkAdding}
                  className="rounded-none bg-white text-foreground hover:bg-white/90 text-sm h-8"
                  data-testid="bulk-add-btn"
                >
                  {bulkAdding ? 'Adding...' : `Add ${bulkSelected.length} to Pipeline`}
                </Button>
                <button onClick={() => setBulkSelected([])} className="text-sm text-white/70 hover:text-white">
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compare action bar */}
      {compareMode && compareList.length >= 2 && (
        <div className="px-6 lg:px-20 py-3 bg-foreground text-background">
          <div className="max-w-[1200px] mx-auto flex items-center justify-between">
            <span className="text-sm">{compareList.length} candidates selected</span>
            <Button
              onClick={() => {
                sessionStorage.setItem('compareList', JSON.stringify(compareList));
                navigate('/compare');
              }}
              className="rounded-none bg-white text-foreground hover:bg-white/90 text-sm h-8"
              data-testid="compare-now-btn"
            >
              Compare Now
            </Button>
          </div>
        </div>
      )}

      {/* Recently Viewed */}
      {!loading && recentlyViewed.length > 0 && !debouncedSearch && activeCount === 0 && (
        <div className="px-6 lg:px-20 py-5 border-b border-border bg-secondary/20">
          <div className="max-w-[1200px] mx-auto">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
              Continue Browsing
            </p>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {recentlyViewed.slice(0, 6).map(c => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/candidate/${c.id}`)}
                  className="flex items-center gap-2 border border-border bg-white px-3 py-2 hover:border-foreground transition-colors flex-shrink-0"
                >
                  {c.photo_url && (
                    <img src={c.photo_url} alt="" className="w-8 h-8 object-cover rounded-sm" />
                  )}
                  <div className="text-left">
                    <p className="text-xs font-medium">{c.full_name}</p>
                    <p className="text-xs text-muted-foreground">{c.core_role}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Candidate Grid */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-20">
        <div className="max-w-[1200px] mx-auto">
          {loading && briefResults === null ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-10">
              {[...Array(9)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : briefLoading ? (
            <div className="text-center py-24">
              <div className="w-6 h-6 border border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-muted-foreground"
                style={{ fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.1em' }}>
                Analysing candidates against your brief...
              </p>
            </div>
          ) : briefResults !== null ? (
            /* AI Talent Brief results */
            briefResults.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-xl font-semibold mb-2">No matches found</p>
                <p className="text-muted-foreground text-sm mb-6">Try a different description or expand your criteria.</p>
                <Button variant="outline" onClick={clearBrief} className="rounded-none">Clear Brief</Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-10">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">{briefResults.length}</span> candidates ranked by brief match
                  </p>
                  <button onClick={clearBrief}
                    className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                    <X className="w-3.5 h-3.5" /> Clear brief
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-10">
                  {briefResults.map((c, idx) => (
                    <div key={c.id} className="relative">
                      {(c.match_score != null || c.final_score != null) && (
                        <div className="absolute top-3 left-3 z-10 bg-foreground text-background px-2 py-0.5 text-xs font-medium"
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          data-testid={`brief-score-${c.id}`}>
                          #{idx + 1} · {Math.round((c.match_score ?? c.final_score) * 10) / 10}
                        </div>
                      )}
                      <CandidateCard
                        candidate={c}
                        isNew={false}
                        compareMode={false}
                        bulkMode={false}
                        onViewed={handleViewed}
                        onDragStart={handleCardDragStart}
                        onDragEnd={handleCardDragEnd}
                        preferredRoles={user?.preferred_roles || []}
                      />
                    </div>
                  ))}
                </div>
              </>
            )
          ) : candidates.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-2xl font-semibold mb-3">No candidates found</p>
              <p className="text-muted-foreground mb-6">Try adjusting your filters.</p>
              <Button variant="outline" onClick={clearFilters} className="rounded-none">
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {fitContext && (
                <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8 bg-[hsl(var(--primary)/0.06)] border border-[hsl(var(--primary)/0.3)] px-4 py-3" data-testid="fit-banner">
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0" />
                    <span className="text-foreground">
                      Ranked by fit for <span className="font-semibold">{fitContext.label}</span>
                      <span className="text-muted-foreground"> — discipline affinity, seniority, skills & experience</span>
                    </span>
                  </div>
                  <button onClick={() => setFitContext(null)}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground flex-shrink-0"
                    data-testid="fit-banner-clear">
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                </div>
              )}
              {fitContext && (
                <p className="text-xs text-muted-foreground/80 mb-4 italic" data-testid="fit-disclaimer">
                  Fit scores use FashionSpyder's proprietary algorithm and AI. AI can make mistakes — use them to
                  support, not replace, your team's judgement.
                </p>
              )}
              <p className="text-sm text-muted-foreground mb-6 sm:mb-10">
                Showing {candidates.length} of {total} candidates
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-10">
                {candidates.map(c => (
                  <CandidateCard
                    key={c.id}
                    candidate={c}
                    isNew={Boolean(c.created_at && c.created_at >= sevenDaysAgo)}
                    compareMode={compareMode}
                    isInCompare={compareList.some(x => x.id === c.id)}
                    onToggleCompare={toggleCompare}
                    bulkMode={bulkMode}
                    isSelected={bulkSelected.some(x => x.id === c.id)}
                    onToggleBulk={toggleBulkSelect}
                    onViewed={handleViewed}
                    onDragStart={handleCardDragStart}
                    onDragEnd={handleCardDragEnd}
                    preferredRoles={user?.preferred_roles || []}
                  />
                ))}
              </div>
              {hasMore && (
                <div className="flex justify-center mt-16">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-10 py-5 rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background"
                  >
                    {loadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Drag-to-pipeline floating panel */}
      {isDragging && (
        <div className="fixed bottom-0 left-0 right-0 z-50" data-testid="drag-pipeline-panel">
          <div className="bg-[#0a0a0a] border-t border-white/10">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-20 py-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/50 text-xs uppercase tracking-widest"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  Drop to add to pipeline
                </p>
                <p className="text-white/30 text-xs truncate ml-4"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {dragCandidate?.full_name}
                </p>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(PIPELINE_STAGES).map(([key, label]) => (
                  <div
                    key={key}
                    onDragOver={e => e.preventDefault()}
                    onDragEnter={() => setDropTarget(key)}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(null);
                    }}
                    onDrop={() => handleDropOnStage(key)}
                    data-testid={`pipeline-drop-${key}`}
                    className={`border py-4 text-center transition-all duration-150 ${
                      dropTarget === key
                        ? 'border-white bg-white/20 text-white'
                        : 'border-white/20 text-white/50 hover:border-white/50 hover:text-white/80'
                    }`}
                  >
                    <p className="text-xs font-medium uppercase tracking-wide"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compare modal */}
      {showCompare && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white z-10">
              <h2 className="font-bold text-lg">Candidate Comparison</h2>
              <button
                onClick={() => setShowCompare(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div
              className="grid border-b border-border"
              style={{ gridTemplateColumns: `128px repeat(${compareList.length}, 1fr)` }}
            >
              <div className="bg-secondary/30 p-4" />
              {compareList.map(c => (
                <div key={c.id} className="p-4 border-l border-border text-center">
                  <div className="w-14 mx-auto mb-2 overflow-hidden bg-secondary" style={{ height: '72px' }}>
                    {c.photo_url && (
                      <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <button
                    onClick={() => { setShowCompare(false); navigate(`/candidate/${c.id}`); }}
                    className="text-sm font-semibold hover:underline"
                  >
                    {c.full_name}
                  </button>
                  <p className="text-xs text-muted-foreground">{c.seniority} {c.core_role}</p>
                </div>
              ))}
            </div>
            <table className="w-full">
              <tbody>
                {[
                  ['Rating',      compareList.map(c => c.overall_ranking ? `★ ${c.overall_ranking}/10` : '—')],
                  ['Location',    compareList.map(c => [c.location_city, c.location_country].filter(Boolean).join(', ') || '—')],
                  ['Seniority',   compareList.map(c => c.seniority || '—')],
                  ['Experience',  compareList.map(c => c.years_experience ? `${c.years_experience} yrs` : '—')],
                  ['Availability',compareList.map(c => c.availability || '—')],
                  ['Work Mode',   compareList.map(c => (c.work_preferences || []).join(', ') || '—')],
                  ['Skills',      compareList.map(c => [
                    ...(c.software_skills || []),
                    ...(c.manual_creative_skills || []),
                  ].map(s => s.name).filter(Boolean).slice(0, 4).join(', ') || '—')],
                  ['Style DNA',   compareList.map(c => (c.ai_style_dna || []).slice(0, 3).join(', ') || '—')],
                  ['Relocate',    compareList.map(c => c.willing_to_relocate ? '✓ Yes' : 'No')],
                ].map(([label, values]) => (
                  <tr key={label} className="border-b border-border">
                    <td className="py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wide bg-secondary/30 w-28">
                      {label}
                    </td>
                    {values.map((v, i) => (
                      <td key={i} className="py-3 px-4 text-sm border-l border-border">{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-4 border-t border-border flex justify-end gap-3">
              <Button
                onClick={bulkAddToPipeline}
                variant="outline"
                className="rounded-none text-xs h-8"
                disabled={compareList.length === 0}
              >
                <BookmarkPlus className="w-3 h-3 mr-1" /> Add all to Pipeline
              </Button>
              <button
                onClick={() => setShowCompare(false)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
